// =============================================================================
// Email Express — hero swap/regen → polished-hero re-render decisions
// (Task #2329). Pure helpers so the rules are unit-testable:
//
//  1. shouldRepolishHeroAfterSwap — after a synchronous hero swap
//     (choose/upload via /hero/set), is the polished hero now STALE (i.e. the
//     editor owes the user an explicit "Apply new image" prompt — swaps never
//     auto-regenerate, per user request)? Only when the polished stack is the
//     canonical view (not edit-draft mode) and a polished render exists.
//
//  2. resolveHeroRegenSettle — the AI hero regenerate is ASYNC: the route flips
//     heroImageStatus → "pending" and a worker renders in the background while
//     the email query polls. The editor arms an "awaiting" flag when the regen
//     is accepted; on each poll tick this resolves whether the render settled
//     and, if it settled ready, whether the hero re-polish should fire now.
//     The flag is cleared on ANY terminal state so a re-polish can never be
//     queued twice for one regeneration (and never fires for a failed render).
// =============================================================================

export type HeroRepolishEmail = {
  polishStatus?: string | null;
  polishBlocks?: { blockKey?: string | null }[] | null;
  heroImageStatus?: string | null;
  heroImageUrl?: string | null;
  heroSwapPending?: boolean | null;
};

/**
 * Derive the hero-picker panel's swap/apply state from the SERVER-persisted
 * `heroSwapPending` flag (set by /hero/set and the hero worker when a polished
 * render exists; cleared by the polish worker only after a successful hero
 * re-render). Because the flag is durable:
 *  - it survives a reload (the Apply affordance always comes back),
 *  - a FAILED apply leaves it set (the worker never cleared it), so the prompt
 *    re-appears instead of silently sending the stale polished hero.
 * While an apply/polish run is in flight, ALL hero controls are locked so a
 * re-select can't race the running worker.
 */
export function heroSwapPanelState(args: {
  heroSwapPending: boolean | null | undefined;
  editMode: boolean;
  /** Any polish run in flight (partial or full), incl. the apply itself. */
  polishPending: boolean;
  /** heroImageActionsBusy(...) — mutations + pending AI hero render. */
  actionsBusy: boolean;
}): { needsApply: boolean; applyBusy: boolean; controlsBusy: boolean } {
  const needsApply = !!args.heroSwapPending && !args.editMode;
  const applyBusy = args.polishPending;
  return {
    needsApply,
    applyBusy,
    controlsBusy: args.actionsBusy || applyBusy,
  };
}

/**
 * True while ANY hero-image action must be locked out. Crucially this includes
 * a server-side `heroImageStatus === "pending"` render (the async AI worker),
 * not just in-flight client mutations: after a regenerate is ACCEPTED the
 * mutation settles while the worker still owns the hero. Allowing a
 * choose/upload in that window races the worker — its late completion would
 * overwrite the explicit selection without re-polishing, leaving the polished
 * preview and the sent email on different images. Every hero action surface
 * (hover chrome, edit panel, polished-stack controls) derives busy from this.
 */
export function heroImageActionsBusy(args: {
  regenPending: boolean;
  setPending: boolean;
  uploadPending: boolean;
  heroImageStatus?: string | null;
}): boolean {
  return (
    args.regenPending ||
    args.setPending ||
    args.uploadPending ||
    args.heroImageStatus === "pending"
  );
}

/**
 * True when a completed hero swap should queue a hero re-polish. Any READY
 * polished render qualifies — including LEGACY manifests whose blocks predate
 * per-block keys: the server either backfills the keys for a targeted partial
 * run or heals via a one-time full re-polish, so the sent polished hero can
 * never be left stale after a swap.
 */
export function shouldRepolishHeroAfterSwap(
  email: HeroRepolishEmail,
  editMode: boolean,
): boolean {
  if (editMode) return false;
  if (email.polishStatus !== "ready") return false;
  return (email.polishBlocks ?? []).length > 0;
}

/**
 * Resolve one poll tick of an in-flight AI hero regeneration.
 * - `repolish`: queue the hero-only partial polish NOW (render settled ready
 *   with an image, and the polished-hero conditions hold).
 * - `clearAwaiting`: the regeneration reached a terminal state (ready or
 *   failed) — disarm the flag so nothing double-fires.
 */
export function resolveHeroRegenSettle(
  awaiting: boolean,
  email: HeroRepolishEmail,
  editMode: boolean,
): { repolish: boolean; clearAwaiting: boolean } {
  if (!awaiting) return { repolish: false, clearAwaiting: false };
  const status = email.heroImageStatus ?? null;
  if (status === "ready" && !!email.heroImageUrl) {
    return {
      repolish: shouldRepolishHeroAfterSwap(email, editMode),
      clearAwaiting: true,
    };
  }
  if (status === "failed") {
    return { repolish: false, clearAwaiting: true };
  }
  // Still pending (or not yet started) — keep waiting.
  return { repolish: false, clearAwaiting: false };
}
